import { Injectable } from '@angular/core';
import { datadogRum } from '@datadog/browser-rum';
import type { LDFlagSet } from 'launchdarkly-js-client-sdk';
import { isAlreadyReported, markReported } from '../error-tracking/recently-reported';
import { getLatestUserRequestTraceId } from './request-trace-ids';

/** Wraps a thrown value as an `Error` so it can be logged and reported consistently. */
export function toError(error: any): Error {
  if (error instanceof Error) {
    return error;
  }
  if (typeof error === 'string') {
    return new Error(error);
  }
  if (error && typeof error === 'object') {
    return new Error(JSON.stringify(error));
  }
  return new Error('Unknown error: ' + String(error));
}

/**
 * Wrapper around Datadog RUM so app code doesn't call the SDK directly.
 * Every method is a no-op until `startRum` has run.
 */
@Injectable({ providedIn: 'root' })
export class RumService {
  /** Identifies the user by the same ID the app sends to Amplitude. Never pass a name or email. */
  public setUser(userId: string): void {
    if (datadogRum.getInitConfiguration()) {
      datadogRum.setUser({ id: userId });
    }
  }

  /** Global context shared by every event, e.g. `userType` and `locationNumber` (camelCase keys). */
  public setGlobalContextProperty(key: string, value: string | boolean): void {
    if (datadogRum.getInitConfiguration()) {
      datadogRum.setGlobalContextProperty(key, value);
    }
  }

  /**
   * Reports an error. `context` (camelCase keys) goes on the event; `groupingHash` becomes the
   * Error Tracking fingerprint, so pass the same value for failures that are one problem.
   */
  public addError(error: unknown, context: Record<string, unknown> = {}): void {
    if (datadogRum.getInitConfiguration()) {
      datadogRum.addError(error, context);
    }
  }

  /** Records a message already reported so an uncaught rethrow of the same message isn't reported again. */
  public markReported(message: string): void {
    markReported(message);
  }

  /** True for failures already reported (see `isAlreadyReported` in recently-reported.ts). */
  public isAlreadyReported(error: unknown): boolean {
    return isAlreadyReported(error);
  }

  /** The Datadog trace ID of the most recent `GET /api/user`, for pages that show a reference ID. */
  public get latestUserRequestTraceId(): string | undefined {
    return getLatestUserRequestTraceId();
  }

  /** Records the current LaunchDarkly flags on the view so errors and slow views can be sliced by flag. */
  public setFeatureFlags(flags: LDFlagSet): void {
    if (!datadogRum.getInitConfiguration()) {
      return;
    }
    Object.entries(flags ?? {}).forEach(([name, value]) => {
      if (['string', 'number', 'boolean'].includes(typeof value)) {
        datadogRum.addFeatureFlagEvaluation(name, value);
      }
    });
  }
}
